import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Sparkles, LogOut, UserCircle, Settings, Zap, UserPlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import { STRIPE_ENABLED } from '../util/constants';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { currentUser, logout, profile, signInAnonymously } = useAuth();
    const navigate = useNavigate();

    // 🔥 State für die Weiterleitung nach anonymem Login
    const [redirectAfterLogin, setRedirectAfterLogin] = useState<string | null>(null);

    // 🔥 useEffect: Sobald ein User existiert und eine Weiterleitung ansteht → navigieren
    useEffect(() => {
        if (currentUser && redirectAfterLogin) {
            navigate(redirectAfterLogin);
            setRedirectAfterLogin(null);
        }
    }, [currentUser, redirectAfterLogin, navigate]);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error('Logout fehlgeschlagen', error);
        }
    };

    const getInitials = () => {
        if (!currentUser?.email) return 'U';
        const email = currentUser.email;
        const namePart = email.split('@')[0];
        return namePart.charAt(0).toUpperCase();
    };

    // 🔥 Geschützte Navigation: bei fehlendem User → anonym anmelden + Weiterleitung merken
    const handleProtectedNavigation = async (path: string) => {
        if (!currentUser) {
            try {
                // 1. Weiterleitung speichern
                setRedirectAfterLogin(path);
                // 2. Anonym anmelden
                await signInAnonymously();
                // 3. Navigation erfolgt dann im useEffect oben
            } catch (error) {
                console.error('Anonymer Login fehlgeschlagen', error);
                alert('Start ohne Anmeldung fehlgeschlagen. Bitte versuche es später erneut.');
                setRedirectAfterLogin(null);
            }
        } else {
            navigate(path);
        }
    };

    return (
        <motion.nav
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
                scrolled
                    ? 'bg-white/80 backdrop-blur-xl shadow-lg py-2'
                    : 'bg-transparent backdrop-blur-sm py-4'
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
                <Link
                    to="/"
                    className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center gap-2"
                >
                    <Sparkles className="w-6 h-6 text-indigo-600" />
                    Studytino
                </Link>

                <div className="hidden md:flex items-center space-x-8">
                    <Link to="/" className="text-gray-700 hover:text-indigo-600 font-medium transition-colors relative group">
                        Home
                        <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-indigo-600 group-hover:w-full transition-all duration-300" />
                    </Link>

                    <button
                        onClick={() => handleProtectedNavigation('/erstellen')}
                        className="text-gray-700 hover:text-indigo-600 font-medium transition-colors relative group"
                    >
                        Skript hochladen
                        <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-indigo-600 group-hover:w-full transition-all duration-300" />
                    </button>

                    <button
                        onClick={() => handleProtectedNavigation('/lernsets')}
                        className="text-gray-700 hover:text-indigo-600 font-medium transition-colors relative group"
                    >
                        Meine Lernsets
                        <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-indigo-600 group-hover:w-full transition-all duration-300" />
                    </button>

                    {currentUser && profile?.tier === 'free' && STRIPE_ENABLED && (
                        <Link
                            to="/upgrade"
                            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-semibold text-sm shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                        >
                            <Zap className="w-4 h-4" />
                            Upgrade auf Pro
                        </Link>
                    )}

                    {currentUser ? (
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold shadow-md hover:shadow-lg transition-shadow focus:outline-none"
                            >
                                {getInitials()}
                            </button>

                            <AnimatePresence>
                                {dropdownOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50"
                                    >
                                        <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                                            {currentUser.email || 'Gast'}
                                        </div>

                                        <Link
                                            to="/profil"
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors flex items-center gap-2"
                                            onClick={() => setDropdownOpen(false)}
                                        >
                                            <UserCircle className="w-4 h-4" />
                                            Profil
                                        </Link>

                                        <Link
                                            to="/einstellungen"
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors flex items-center gap-2"
                                            onClick={() => setDropdownOpen(false)}
                                        >
                                            <Settings className="w-4 h-4" />
                                            Einstellungen
                                        </Link>

                                        {profile?.tier === 'free' && STRIPE_ENABLED && (
                                            <Link
                                                to="/upgrade"
                                                className="block px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition-colors flex items-center gap-2"
                                                onClick={() => setDropdownOpen(false)}
                                            >
                                                <Zap className="w-4 h-4" />
                                                Upgrade auf Pro
                                            </Link>
                                        )}

                                        {currentUser.isAnonymous ? (
                                            <Link
                                                to="/register"
                                                className="block px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center gap-2"
                                                onClick={() => setDropdownOpen(false)}
                                            >
                                                <UserPlus className="w-4 h-4" />
                                                Konto erstellen (Daten sichern)
                                            </Link>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    handleLogout();
                                                    setDropdownOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors flex items-center gap-2"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                Abmelden
                                            </button>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ) : (
                        <Link to="/login" className="text-gray-700 hover:text-indigo-600 font-medium transition-colors relative group">
                            Login
                            <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-indigo-600 group-hover:w-full transition-all duration-300" />
                        </Link>
                    )}
                </div>

                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="md:hidden text-gray-700 hover:text-indigo-600 focus:outline-none relative z-50"
                    aria-label="Menü öffnen"
                >
                    <AnimatePresence mode="wait">
                        {isOpen ? (
                            <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                                <X size={28} />
                            </motion.div>
                        ) : (
                            <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                                <Menu size={28} />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </button>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="md:hidden overflow-hidden"
                    >
                        <div className="bg-white/95 backdrop-blur-md mt-2 py-6 px-6 space-y-4 shadow-xl rounded-b-2xl border-t border-gray-100">
                            <Link to="/" className="block py-2 text-gray-700 hover:text-indigo-600 text-lg font-medium transition-colors" onClick={() => setIsOpen(false)}>
                                Home
                            </Link>
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    handleProtectedNavigation('/erstellen');
                                }}
                                className="block w-full text-left py-2 text-gray-700 hover:text-indigo-600 text-lg font-medium transition-colors"
                            >
                                Skript hochladen
                            </button>
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    handleProtectedNavigation('/lernsets');
                                }}
                                className="block w-full text-left py-2 text-gray-700 hover:text-indigo-600 text-lg font-medium transition-colors"
                            >
                                Meine Lernsets
                            </button>

                            {currentUser && profile?.tier === 'free' && STRIPE_ENABLED && (
                                <Link
                                    to="/upgrade"
                                    className="block py-2 text-amber-600 hover:text-amber-700 text-lg font-medium transition-colors flex items-center gap-2"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <Zap className="w-5 h-5" />
                                    Upgrade auf Pro
                                </Link>
                            )}

                            {currentUser ? (
                                <>
                                    <div className="py-2 text-gray-600 flex items-center gap-2">
                                        <UserCircle className="w-5 h-5" />
                                        {currentUser.email || 'Gast'}
                                    </div>
                                    <Link to="/profil" className="block py-2 text-gray-700 hover:text-indigo-600 text-lg font-medium transition-colors" onClick={() => setIsOpen(false)}>
                                        Profil
                                    </Link>
                                    <Link to="/einstellungen" className="block py-2 text-gray-700 hover:text-indigo-600 text-lg font-medium transition-colors" onClick={() => setIsOpen(false)}>
                                        Einstellungen
                                    </Link>

                                    {currentUser.isAnonymous ? (
                                        <Link
                                            to="/register"
                                            className="block py-2 text-indigo-600 hover:text-indigo-700 text-lg font-medium transition-colors flex items-center gap-2"
                                            onClick={() => setIsOpen(false)}
                                        >
                                            <UserPlus className="w-5 h-5" />
                                            Konto erstellen (Daten sichern)
                                        </Link>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                handleLogout();
                                                setIsOpen(false);
                                            }}
                                            className="block w-full text-left py-2 text-gray-700 hover:text-indigo-600 text-lg font-medium transition-colors"
                                        >
                                            Logout
                                        </button>
                                    )}
                                </>
                            ) : (
                                <Link to="/login" className="block py-2 text-gray-700 hover:text-indigo-600 text-lg font-medium transition-colors" onClick={() => setIsOpen(false)}>
                                    Login
                                </Link>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.nav>
    );
};

export default Navbar;